import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import OfferCard from './OfferCard';
import './Profile.css';

interface ProfileData {
  _id: string;
  username: string;
  nickname: string;
  cta: string;
  image: string | null;
  show_button?: boolean;
  isPreview?: boolean;
  isLive?: boolean;
}

interface Stats {
  totalEarnings: number;
  networkEarnings: number;
  completedOffers: number;
  referralCount: number;
}

interface Offer {
  id: string;
  name: string;
  description: string;
  icon: string | null;
  type: string;
  action: string;
  featured?: boolean;
  payout?: number;
  category?: string;
}

interface ProfileProps {
  mode: 'preview' | 'live';
  onProfileLoad?: (showButton: boolean, userId?: string, hasOffers?: boolean) => void;
}

const Profile: React.FC<ProfileProps> = ({ mode, onProfileLoad }) => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ngrok URL for development - change this to your production URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gemdropserver-production.up.railway.app';

  useEffect(() => {
    const fetchData = async () => {
      if (!username) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch profile data
        const profileResponse = await axios.get(
          `${API_BASE_URL}/api/public/gotap/profile/${username}?mode=${mode}`
        );

        if (profileResponse.data.success) {
          setProfile(profileResponse.data.profile);
          setStats(profileResponse.data.stats);
        }

        // Detect platform from user agent
        const userAgent = navigator.userAgent.toLowerCase();
        const platform = /android/i.test(userAgent) ? 'android' : 'ios';

        // Fetch offers with cookie support for rotation
        const offersResponse = await axios.get(
          `${API_BASE_URL}/api/public/gotap/offers?platform=${platform}`,
          {
            withCredentials: true // Enable cookies for offer rotation
          }
        );

        if (offersResponse.data.success) {
          setOffers(offersResponse.data.offers);

          // Notify parent component about show_button setting, user ID, and whether there are offers
          if (onProfileLoad && profileResponse.data.success) {
            // Only show button if explicitly set to true AND there are offers
            const showButton = profileResponse.data.profile.show_button === true;
            const userId = profileResponse.data.profile._id;
            const hasOffers = offersResponse.data.offers.length > 0;
            onProfileLoad(showButton, userId, hasOffers);
          }
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 404) {
          setError('Profile not found');
        } else {
          setError('Failed to load profile');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username, mode]);

  if (loading) {
    return (
      <div className="profile-container loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container error">
        <div className="error-message">
          <h2>{error}</h2>
          <p>Please check the username and try again</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container error">
        <div className="error-message">
          <h2>Profile not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        {/* Profile Image */}
        <div className="profile-image-container">
          {profile.image ? (
            <img src={profile.image} alt={profile.nickname} className="profile-image" />
          ) : (
            <div className="profile-image-placeholder">
              <span>{profile.nickname?.charAt(0)?.toUpperCase() || 'U'}</span>
            </div>
          )}
        </div>

        {/* Profile Info */}
        <h1 className="profile-nickname">{profile.nickname || 'Demo Profile'}</h1>
        <p className="profile-cta">{profile.cta || 'Demo call to action'}</p>

      </div>

      {/* Offers Section */}
      <div className="offers-section">
        {offers.length === 0 ? (
          <div className="no-offers-banner">
            <div className="banner-icon">🎮</div>
            <h2 className="banner-title">
              Join {profile.nickname || 'us'} on Gemdrop to earn rewards for playing games!
            </h2>
            <p className="banner-description">
              More offers coming soon. Check back later!
            </p>
            <button
              className="banner-cta-button"
              onClick={() => {
                const botUrl = profile._id
                  ? `https://t.me/gemdrop_app_bot?start=ref_${profile._id}`
                  : 'https://t.me/gemdrop_app_bot';
                window.open(botUrl, '_blank');
              }}
            >
              Join Gemdrop
            </button>
          </div>
        ) : (
          <div className="offers-container">
            {offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} userId={profile._id} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default Profile;